// The Breakpointer component allows for the dynamic application of breakpoint classes to an element.
// As a target element changes width it will have class modifiers (BEM style) applied to the target class name.
// For example an element with class 'foo' will gain the 'foo--S' as it increases past the "small" breakpoint size.
// And it will gain the 'foo-M' and 'foo-L' as its width increases past the "medium" and "large" breakpoint sizes.
// Each class modifier is retained as each breakpoint is past. Resulting in the target element having multiple modifiers. For example:
// ```
//     <div class="breakpointer foo foo--S foo--M foo--L">blah</div>
// ```
//
// Configuration
// Apply the `breakpointer` class to the element to control.
// A JSON configuration string can be passed via the `data-breakpointer` data attribute:
// ```
//    <div class="breakpointer foo" data-breakpointer="{"class":"foo","breakpoints":{"M":500}}">…</div>
// ```
// Note that this JSON string will need to be escaped (not shown in this example).
// It's easy to construct the configuration object as a Ruby hash and then call `<%= jda({:class => "foo"}) %>` in the erb view to escape the JSON.
// A "class" key/value configuration is the only required option.
// Optionally a "breakpoints" key can be configured - an object containing breakpoint labels (keys) and the breakpoint (values).
// The breakpoint value can be a number representing the width in pixels or a CSS value string. For example: "500" or "20rem".
// Any number of breakpoints can be provided. For example:
// ```
// { "class":"foo", "breakpoints":{ "gt200":200, "gt500":500 } }
// ```
// In that example the labels "gt200" and "gt500" will be applied as class modifiers as the element's width is greater that 200px and then greater than 500px.
// Optionally the value can be an array of two elements representing the start and end of the break. For example:
// ```
// { "class":"foo", "breakpoints":{ "narrow":[0,200], "wide":"500rem" } }
// ```
// In that example the "narrow" class modifier will only be applied when the width is 0…200 pixels. The "wide" modifier is applied on or above 500 rem.
//
// Usage
// Once configured it's a matter of applying CSS rules based on these breakpoint class modifiers. Here's a SCSS example:
// ```
// .foo {
//     // base rules
//     &.foo--gt200 {
//         // rules for foo greater than 200px wide
//     }
//     &.foo--gt500 {
//         // rules for foo greater than 500px wide
//     }
// }
// ```
import Component from "../../lib/component";
import { getThrottledFunction } from "../../helpers/throttle";
import BreakpointComparer from "./breakpoint_comparer";

export const BreakpointerConfig = {
  componentTypeKey: "Breakpointer",
  baseHtmlClass: "breakpointer",
  selector: ".breakpointer",
};

const DefaultBreakpoints = {
  L: 900, // approx 69rem
  M: 600, // approx 46rem
  S: 300, // approx 23rem
};

export class Breakpointer extends Component {
  constructor(id_or_element) {
    super({
      element: id_or_element,
      config: BreakpointerConfig,
    });

    this.config = this.getConfig();
    this.targetClassName = this.getTargetClassName();
    this.breakpoints = this.getBreakpoints();

    this.width = this.getWidth();
    this.breakpointLabels = this.getBreakpointLabels();

    this.comparer = new BreakpointComparer(this.breakpoints);

    const resizeHandler = getThrottledFunction(
      this.handleResize.bind(this),
      100,
    );
    this.observer = new ResizeObserver(resizeHandler);
    this.observer.observe(this.element);
  }

  getConfig() {
    let config = this.element.dataset["breakpointer"];
    if (config) return JSON.parse(config);
    return {};
  }

  getTargetClassName() {
    return this.config["class"] || "";
  }

  getBreakpoints() {
    if (this.config && this.config["breakpoints"])
      return this.config["breakpoints"];
    return DefaultBreakpoints;
  }

  handleResize(event) {
    this.width = this.getWidth();
    this.breakpointLabels = this.getBreakpointLabels();
    this.render();
  }

  getWidth() {
    return this.element.offsetWidth;
  }

  getBreakpointLabels() {
    let labels = [];
    const keys = Object.keys(this.breakpoints);
    keys.forEach((key) => {
      if (this.comparer && this.comparer.isWithinBreakpoint(this.width, key)) labels.push(key);
    });
    return labels;
  }

  render() {
    this.renderClasses();
  }

  renderClasses() {
    this.clearAllClasses();
    this.addClasses();
  }

  clearAllClasses() {
    Object.keys(this.breakpoints).forEach((key) => {
      this.element.classList.remove(`${this.targetClassName}--${key}`);
    });
  }

  addClasses() {
    this.breakpointLabels.forEach((breakpointLabel) => {
      this.element.classList.add(`${this.targetClassName}--${breakpointLabel}`);
    });
  }
}
